import Enum from '@square/dashboard-utilities/utils/enum';
import GlobalAddress from '@square/dashboard-data/models/squareup/common/location/global-address';
import DateTime from '@square/dashboard-data/models/squareup/common/time/date-time';
import GroupV2 from '@square/dashboard-data/models/squareup/rolodex/group-v2';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
export const AttributeType = new Enum('ADDRESS', 'BOOLEAN', 'DATE', 'EMAIL', 'ENUM', 'NUMBER', 'PHONE', 'TEXT');
export const AttributeVisibility = new Enum('VISIBILITY_UNKNOWN', 'VISIBILITY_HIDDEN', 'VISIBILITY_READ', 'VISIBILITY_READ_WRITE');
export const DEFAULT_ATTRIBUTE_BIRTHDAY = 'default:birthday';
export const DEFAULT_ATTRIBUTE_COMPANY = 'default:company';
export const DEFAULT_ATTRIBUTE_TAX_ID = 'default:tax_id';
export const DEFAULT_ATTRIBUTE_REFERENCE_ID = 'default:reference_id';
export const DEFAULT_ATTRIBUTE_NAME = 'default:name';
export const DEFAULT_ATTRIBUTE_PHONE_NUMBER = 'default:phone_number';
export const DEFAULT_ATTRIBUTE_EMAIL_ADDRESS = 'default:email_address';
export const DEFAULT_ATTRIBUTE_ADDRESS = 'default:address';
export const DEFAULT_ATTRIBUTE_GROUPS = 'default:groups';
export const DEFAULT_ATTRIBUTE_APPOINTMENT_TIMEZONE = 'default:appointment_timezone';
export const DEFAULT_ATTRIBUTE_APPOINTMENT_NOTIFICATION_PREFERENCE = 'default:appointment_notification_preference';
export const DEFAULT_ATTRIBUTE_KEYS = [DEFAULT_ATTRIBUTE_BIRTHDAY, DEFAULT_ATTRIBUTE_COMPANY, DEFAULT_ATTRIBUTE_TAX_ID, DEFAULT_ATTRIBUTE_REFERENCE_ID];

// Extra default fields define the keys for additional default column we support in Custom Field V2
export const EXTRA_DEFAULT_ATTRIBUTE_KEYS = [DEFAULT_ATTRIBUTE_NAME, DEFAULT_ATTRIBUTE_PHONE_NUMBER, DEFAULT_ATTRIBUTE_EMAIL_ADDRESS, DEFAULT_ATTRIBUTE_ADDRESS, DEFAULT_ATTRIBUTE_GROUPS, DEFAULT_ATTRIBUTE_APPOINTMENT_TIMEZONE, DEFAULT_ATTRIBUTE_APPOINTMENT_NOTIFICATION_PREFERENCE];
export function getAttributeTranslationKeyByType(type) {
  switch (type) {
    case AttributeType.ADDRESS:
      return 'customers.list.attributes.types.address';
    case AttributeType.BOOLEAN:
      return 'customers.list.attributes.types.boolean';
    case AttributeType.DATE:
      return 'customers.list.attributes.types.date';
    case AttributeType.ENUM:
      return 'customers.list.attributes.types.enum';
    case AttributeType.EMAIL:
      return 'customers.list.attributes.types.email';
    case AttributeType.NUMBER:
      return 'customers.list.attributes.types.number';
    case AttributeType.PHONE:
      return 'customers.list.attributes.types.phone';
    case AttributeType.TEXT:
      return 'customers.list.attributes.types.text';
    default:
      return null;
  }
}
export function getAttributeValue(localeTime, attribute, intl) {
  // eslint-disable-next-line default-case
  switch (attribute.get('type')) {
    case AttributeType.BOOLEAN:
      return attribute.get('boolean') ? intl.t('common.yes') : intl.t('common.no');
    case AttributeType.DATE:
      {
        // date value is encoded in UTC timestamp with time portion zeroed out
        const dateValue = attribute.get('dateValue');
        return localeTime.dateTime(new Date(dateValue.getUTCFullYear(), dateValue.getUTCMonth(), dateValue.getUTCDate()), 'longCardinalDate');
      }
    case AttributeType.ENUM:
      return attribute.get('enumValue');
    case AttributeType.EMAIL:
      return attribute.get('emailValue');
    case AttributeType.NUMBER:
      return attribute.get('numberValue');
    case AttributeType.PHONE:
      return attribute.get('phoneValue');
    case AttributeType.TEXT:
      return attribute.get('text');
  }
  return attribute.get('fallbackValue');
}
export const AttributeDefinition = ProtoBaseModel.extend({
  key: attr(String),
  name: attr(String),
  type: attr(AttributeType),
  isVisibleInProfile: attr(Boolean),
  isHidden: attr(Boolean),
  isDefault: attr(Boolean),
  isSelectedColumn: attr(Boolean),
  isConfigurable: attr(Boolean, {
    defaultValue: true
  }),
  owner: attr(String),
  visibility: attr(AttributeVisibility),
  // enum
  enumValues: repeated(String),
  enumAllowMultiple: attr(Boolean)
});
export const AttributeSchema = ProtoBaseModel.extend({
  version: attr(String),
  attributeDefinitions: repeated(AttributeDefinition),
  attributeKeysInOrder: repeated(String)
});
export const Attribute = ProtoBaseModel.extend({
  key: attr(String),
  name: attr(String),
  type: attr(AttributeType),
  fallbackValue: attr(String),
  isVisibleInProfile: attr(Boolean),
  text: attr(String),
  numberValue: attr(String),
  boolean: attr(Boolean),
  enumValue: repeated(String),
  emailValue: attr(String),
  phoneValue: attr(String),
  addressValue: attr(GlobalAddress),
  dateValue: attr(DateTime),
  // custom serializer passes all unknown fields
  // back to the server. if the _data attribute holds
  // a field that isn't declared with `attr`, copy it
  // back to the json object as-is.
  toJSON(...args) {
    const data = this._super(...args);
    const serializedKeys = Object.keys(data);
    for (const key in this._data) {
      if (!serializedKeys.includes(key)) {
        data[key] = this._data[key];
      }
    }
    return data;
  }
});
export const CheckAttributeDefinitionUsageRequest = ProtoBaseModel.extend({
  merchantToken: attr(String),
  schemaVersion: attr(String),
  attributeDefinition: attr(AttributeDefinition)
});
export const CheckAttributeDefinitionUsageResponse = ProtoBaseModel.extend({
  isInUse: attr(Boolean),
  groups: repeated(GroupV2)
});
export const DeleteAttributeDefinitionRequest = ProtoBaseModel.extend({
  merchantToken: attr(String),
  schemaVersion: attr(String),
  attributeDefinition: attr(AttributeDefinition)
});
export const DeleteAttributeDefinitionResponse = ProtoBaseModel.extend({
  schemaVersion: attr(String)
});
export const UpdateAttributeSchemaRequest = ProtoBaseModel.extend({
  schemaVersion: attr(String),
  attributeDefinitions: repeated(AttributeDefinition),
  attributeKeysInOrder: repeated(String)
});
export const UpdateAttributeSchemaResponse = ProtoBaseModel.extend({
  attributeSchema: attr(AttributeSchema)
});
export const statesListJPStringMap = {
  '01': 'statesSelect.statesList.JP.01',
  '02': 'statesSelect.statesList.JP.02',
  '03': 'statesSelect.statesList.JP.03',
  '04': 'statesSelect.statesList.JP.04',
  '05': 'statesSelect.statesList.JP.05',
  '06': 'statesSelect.statesList.JP.06',
  '07': 'statesSelect.statesList.JP.07',
  '08': 'statesSelect.statesList.JP.08',
  '09': 'statesSelect.statesList.JP.09',
  10: 'statesSelect.statesList.JP.10',
  11: 'statesSelect.statesList.JP.11',
  12: 'statesSelect.statesList.JP.12',
  13: 'statesSelect.statesList.JP.13',
  14: 'statesSelect.statesList.JP.14',
  15: 'statesSelect.statesList.JP.15',
  16: 'statesSelect.statesList.JP.16',
  17: 'statesSelect.statesList.JP.17',
  18: 'statesSelect.statesList.JP.18',
  19: 'statesSelect.statesList.JP.19',
  20: 'statesSelect.statesList.JP.20',
  21: 'statesSelect.statesList.JP.21',
  22: 'statesSelect.statesList.JP.22',
  23: 'statesSelect.statesList.JP.23',
  24: 'statesSelect.statesList.JP.24',
  25: 'statesSelect.statesList.JP.25',
  26: 'statesSelect.statesList.JP.26',
  27: 'statesSelect.statesList.JP.27',
  28: 'statesSelect.statesList.JP.28',
  29: 'statesSelect.statesList.JP.29',
  30: 'statesSelect.statesList.JP.30',
  31: 'statesSelect.statesList.JP.31',
  32: 'statesSelect.statesList.JP.32',
  33: 'statesSelect.statesList.JP.33',
  34: 'statesSelect.statesList.JP.34',
  35: 'statesSelect.statesList.JP.35',
  36: 'statesSelect.statesList.JP.36',
  37: 'statesSelect.statesList.JP.37',
  38: 'statesSelect.statesList.JP.38',
  39: 'statesSelect.statesList.JP.39',
  40: 'statesSelect.statesList.JP.40',
  41: 'statesSelect.statesList.JP.41',
  42: 'statesSelect.statesList.JP.42',
  43: 'statesSelect.statesList.JP.43',
  44: 'statesSelect.statesList.JP.44',
  45: 'statesSelect.statesList.JP.45',
  46: 'statesSelect.statesList.JP.46',
  47: 'statesSelect.statesList.JP.47'
};